import { Box, Link, Typography } from "@mui/material";
import { ConsortiumMemberBadge } from "@/features/consortiumBadge/ConsortiumMemberBadge";
import { Link as RouterLink } from "react-router-dom";
import { CTextFieldPassword } from "@/components/TextField/CTextFieldPassword";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { Divider } from "@/components/Divider";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle, SectionTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import { ConsumeCredit } from "./components/ConsumeCredit";
import { useFeatureFlags } from "@/configs/featureFlag";
import { UserConfig } from "@/features/accountSettings/components/UserConfig";

import { type UseFormReturn } from "react-hook-form";
import { type ChangePasswordFormData } from "./types/schemas";
import { PropertySummaryCompanyInfo } from "@/features/accountSettings/components/PropertySummaryCompanyInfo";
import { PropertySummaryCompanyLogo } from "@/features/accountSettings/components/PropertySummaryCompanyLogo";
import { ClientConsumeCredit } from "./components/ClientConsumeCredit";
import { DmUsage } from "./components/DmUsage";
import { ClientDmUsage } from "./components/ClientDmUsage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { SalesforceConnectionSection } from "@/features/salesforceIntegration/components/SalesforceConnectionSection";
import { SansanConnectionSection } from "@/features/sansanIntegration/components/SansanConnectionSection";
import {
  MONTHLY_MEMBER_BADGE_LABEL,
  BrandBadge,
  useIsMonthlyMember,
} from "@/features/brandBadge";

interface IAccountSettingsPresenterProps {
  form: UseFormReturn<ChangePasswordFormData>;
  handleSubmit: (data: ChangePasswordFormData) => Promise<void>;
}
export const AccountSettingsPresenter: React.FC<
  IAccountSettingsPresenterProps
> = ({ form, handleSubmit }) => {
  const user = useCurrentUser();
  const { mypageTop, propertySummary } = useFeatureFlags();
  const isMonthlyMember = useIsMonthlyMember();
  return (
    <PagePaper>
      <form>
        {/* ページタイトル（H2） */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ContactPageIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>アカウント情報</PageTitle>
          {/* コンソーシアム会員バッジ: 所属クライアントが共同コンソーシアム企業のときのみ表示 */}
          {user?.isConsortiumMember === true && (
            <Box sx={{ ml: 2 }}>
              <ConsortiumMemberBadge size="medium" />
            </Box>
          )}
        </Box>

        {/* 月額会員バッジ。CEO指定によりページ見出し(H2)とその下の見出し(H3)の下に配置する。
            表示は有効な月額プラン保持ユーザーのみ（useIsMonthlyMember）。 */}
        {isMonthlyMember && (
          <Box sx={{ mb: 3, mt: 1 }}>
            <BrandBadge label={MONTHLY_MEMBER_BADGE_LABEL} />
          </Box>
        )}
        {!isMonthlyMember && <Box sx={{ mb: 3 }} />}

        {/* 企業の消費クレジット */}
        {user?.role === "admin" && user?.clientId != null && (
          <>
            <Box sx={{ display: "flex", mb: 3 }}>
              <ClientConsumeCredit />
            </Box>
            <Box sx={{ display: "flex", mb: 3 }}>
              <ClientDmUsage />
            </Box>
          </>
        )}

        {/* ユーザーの消費クレジット */}
        <Box sx={{ display: "flex", mb: 3 }}>
          <ConsumeCredit />
        </Box>
        <Box sx={{ display: "flex", mb: 3 }}>
          <DmUsage />
        </Box>

        <SectionPaper>
          <SectionTitle>パスワードの変更</SectionTitle>
          <Divider />
          {/* パスワード変更 */}
          <Box sx={{ m: 2 }}>
            <Box sx={{ mb: 2 }}>
              <CTextFieldPassword
                control={form.control}
                name="currentPassword"
                label="現在のパスワード"
                autoComplete="current-password"
                size={SizingWrapperStyle.LARGE}
                required={false} // 必須が明確なので必須マークは不要
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <CTextFieldPassword
                control={form.control}
                name="newPassword"
                label="新しいパスワード"
                autoComplete="new-password"
                size={SizingWrapperStyle.LARGE}
                required={false} // 必須が明確なので必須マークは不要
              />
            </Box>

            <CTextFieldPassword
              control={form.control}
              name="newPasswordConfirmation"
              label="新しいパスワード（確認）"
              autoComplete="new-password"
              size={SizingWrapperStyle.LARGE}
              required={false} // 必須が明確なので必須マークは不要
            />
          </Box>

          <Divider />

          {/* 保存ボタン */}
          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              label="保存"
              onClick={() => {
                void form.handleSubmit(handleSubmit)();
              }}
              variant={ButtonVariantOption.Contained}
              size={SizingWrapperStyle.SMALL}
            />
          </Box>
        </SectionPaper>

        <Box sx={{ display: "flex", my: 3 }}>
          <UserConfig />
        </Box>

        {propertySummary && (
          <>
            <Box sx={{ display: "flex", my: 3 }}>
              <PropertySummaryCompanyInfo />
            </Box>

            <Box sx={{ display: "flex", my: 3 }}>
              <PropertySummaryCompanyLogo />
            </Box>
          </>
        )}

        {/* Salesforce連携（表示条件はコンポーネント内部で判定する） */}
        <Box sx={{ display: "flex", my: 3 }}>
          <SalesforceConnectionSection />
        </Box>

        {/* Sansan連携（表示条件はコンポーネント内部で判定する） */}
        <Box sx={{ display: "flex", my: 3 }}>
          <SansanConnectionSection />
        </Box>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={mypageTop ? "/mypage" : "/feed"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>
                {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
              </Typography>
            </Box>
          </Link>
        </Box>
      </form>
    </PagePaper>
  );
};
